import {
  parsePhoneNumberFromString,
  Metadata,
} from "libphonenumber-js";

export interface PhoneCountryCode {
  /** Código de marcación sin el "+" (así se guarda en vtf_vch_telefonocodigopais) */
  code: string;
  name: string;
  flag: string;
}

export const DEFAULT_PHONE_COUNTRY_CODE = "51";

/**
 * Países ofrecidos en el selector. Solo datos de presentación: los largos
 * válidos y la validación en sí salen de libphonenumber-js (metadata oficial
 * de Google), no de una tabla escrita a mano.
 *
 * Se exporta ordenado alfabéticamente, así que un país nuevo se puede agregar
 * en cualquier posición de este arreglo.
 */
const COUNTRY_TABLE: PhoneCountryCode[] = [
  { code: "49", name: "Alemania", flag: "🇩🇪" },
  { code: "54", name: "Argentina", flag: "🇦🇷" },
  { code: "61", name: "Australia", flag: "🇦🇺" },
  { code: "591", name: "Bolivia", flag: "🇧🇴" },
  { code: "55", name: "Brasil", flag: "🇧🇷" },
  { code: "56", name: "Chile", flag: "🇨🇱" },
  { code: "86", name: "China", flag: "🇨🇳" },
  { code: "57", name: "Colombia", flag: "🇨🇴" },
  { code: "506", name: "Costa Rica", flag: "🇨🇷" },
  { code: "53", name: "Cuba", flag: "🇨🇺" },
  { code: "593", name: "Ecuador", flag: "🇪🇨" },
  { code: "1", name: "EE.UU. / Canadá", flag: "🇺🇸" },
  { code: "503", name: "El Salvador", flag: "🇸🇻" },
  { code: "34", name: "España", flag: "🇪🇸" },
  { code: "33", name: "Francia", flag: "🇫🇷" },
  { code: "502", name: "Guatemala", flag: "🇬🇹" },
  { code: "504", name: "Honduras", flag: "🇭🇳" },
  { code: "39", name: "Italia", flag: "🇮🇹" },
  { code: "81", name: "Japón", flag: "🇯🇵" },
  { code: "52", name: "México", flag: "🇲🇽" },
  { code: "505", name: "Nicaragua", flag: "🇳🇮" },
  { code: "507", name: "Panamá", flag: "🇵🇦" },
  { code: "595", name: "Paraguay", flag: "🇵🇾" },
  { code: "51", name: "Perú", flag: "🇵🇪" },
  { code: "351", name: "Portugal", flag: "🇵🇹" },
  { code: "44", name: "Reino Unido", flag: "🇬🇧" },
  { code: "598", name: "Uruguay", flag: "🇺🇾" },
  { code: "58", name: "Venezuela", flag: "🇻🇪" },
];

export const PHONE_COUNTRY_CODES: PhoneCountryCode[] = [...COUNTRY_TABLE].sort(
  (a, b) => a.name.localeCompare(b.name, "es")
);

/** Máximo absoluto de E.164: 15 dígitos contando el código de país */
const E164_MAX_DIGITS = 15;

const normalize = (code?: string | number): string =>
  String(code ?? "").replace(/\D/g, "");

export const getPhoneCountry = (
  code?: string | number
): PhoneCountryCode | undefined =>
  PHONE_COUNTRY_CODES.find((country) => country.code === normalize(code));

/**
 * Largos posibles del número nacional según la metadata de libphonenumber-js.
 * Devuelve [] si el código de país no tiene plan de numeración conocido.
 */
export const getPossibleLengths = (code?: string | number): number[] => {
  const dial = normalize(code);
  if (!dial) return [];
  try {
    const metadata = new Metadata();
    // `selectNumberingPlan` está tipado para recibir el ISO del país, pero en
    // runtime acepta también el código de marcación ("Supports just passing
    // `callingCode` as the first argument"). Usamos eso porque un mismo código
    // puede corresponder a varios países (p. ej. +1 → US, CA, DO).
    metadata.selectNumberingPlan(dial as Parameters<
      Metadata["selectNumberingPlan"]
    >[0]);
    return metadata.numberingPlan?.possibleLengths() ?? [];
  } catch {
    // `Metadata` es API semi-interna de la librería; si cambia, se cae al
    // tope genérico de E.164 en vez de romper el formulario.
    return [];
  }
};

/**
 * Tope de dígitos para el input. Es el largo real más grande que admite el
 * país, así que nunca corta un número válido.
 */
export const getPhoneMaxLength = (code?: string | number): number => {
  const lengths = getPossibleLengths(code);
  if (lengths.length > 0) return Math.max(...lengths);
  return Math.max(1, E164_MAX_DIGITS - normalize(code).length);
};

/** Devuelve el mensaje de error o null si el número es válido (vacío = válido) */
export const validatePhoneNumber = (
  phoneNumber: string,
  code?: string | number
): string | null => {
  if (!phoneNumber) return null;

  if (!/^\d+$/.test(phoneNumber)) {
    return "El teléfono solo debe contener números";
  }

  const dial = normalize(code);
  const parsed = parsePhoneNumberFromString(`+${dial}${phoneNumber}`);
  if (parsed?.isValid()) return null;

  const country = getPhoneCountry(dial);
  const pais = country ? ` para ${country.name}` : "";

  // Si el largo no está entre los posibles, se lo decimos explícitamente;
  // enumerar solo tiene sentido cuando son pocas opciones (EE.UU.: [10],
  // Perú: [8, 9]). Alemania admite de 4 a 15 y listarlas no ayudaría.
  const lengths = getPossibleLengths(dial);
  if (lengths.length > 0 && !lengths.includes(phoneNumber.length)) {
    if (lengths.length === 1) {
      return `El teléfono debe tener exactamente ${lengths[0]} dígitos${pais}`;
    }
    if (lengths.length <= 3) {
      const enumerado = `${lengths.slice(0, -1).join(", ")} o ${
        lengths[lengths.length - 1]
      }`;
      return `El teléfono debe tener ${enumerado} dígitos${pais}`;
    }
    return `El teléfono tiene ${phoneNumber.length} dígitos, que no es un largo válido${pais}`;
  }

  return `El teléfono no es un número válido${pais}`;
};

/**
 * Opciones para <select> (SelectInput espera { value, name } con name string,
 * por eso la bandera va como emoji dentro del texto y no como <img>: así el
 * combo de edición se ve igual que el de creación).
 */
export const PHONE_COUNTRY_SELECT_ITEMS = PHONE_COUNTRY_CODES.map(
  (country) => ({
    value: country.code,
    // Los espacios duros evitan que el navegador los colapse y dan al emoji
    // una separación parecida al margin del selector de creación.
    name: `${country.flag}  +${country.code} ${country.name}`,
  })
);
